import { useState } from 'react';
import { ArrowRight, Award, Banknote, Check, ChevronDown, ChevronRight, Moon, PackageCheck, Sunrise } from 'lucide-react';
import { useData, useApp } from '../context.jsx';
import { getTodayKey, getCurrentWeekDay } from '../lib/helpers.js';
import { normalizeReflection } from '../lib/normalizers.js';
import { CHORE_CADENCES, MORNING_REFLECTIONS, EVENING_REFLECTIONS } from '../lib/constants.js';
import { Metric, Progress } from '../components/ui.jsx';
import { TaskRow } from '../components/TaskRow.jsx';
import { StorePreview } from '../components/StorePreview.jsx';

function BudgetTrackerRow({ item }) {
  const percent = item.budgeted ? Math.min((item.actual / item.budgeted) * 100, 140) : 0;
  const over = item.actual > item.budgeted;
  return (
    <div className="budget-tracker-row">
      <div>
        <strong>{item.subcategory}</strong>
        <span>${item.actual.toFixed(0)} / ${item.budgeted.toFixed(0)}</span>
      </div>
      <small>{item.category}</small>
      <div className="budget-mini-track">
        <span className={over ? 'over' : ''} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  );
}

function ReadOnlyReflectionPanel({ icon: Icon, title, eyebrow, prompts, values }) {
  return (
    <section className="panel reflection-panel readonly-reflection-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        <Icon size={24} />
      </div>
      <div className="readonly-reflection-stack">
        {prompts.map((item) => (
          <div className="readonly-reflection-field" key={item.key}>
            <span>{item.label}</span>
            <p>{values[item.key] || item.prompt}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { data, updateData } = useData();
  const { stats, setActiveView, toggleChore } = useApp();
  const [openRoom, setOpenRoom] = useState('');
  const [dailyOpen, setDailyOpen] = useState(false);
  const today = getTodayKey();
  const reflection = data.planner.reflections[today] || normalizeReflection();

  const dailyChores = data.chores
    .filter((item) => item.cadence === 'Daily')
    .sort((a, b) => `${a.room} ${a.title}`.localeCompare(`${b.room} ${b.title}`));

  const scheduledChores = data.chores
    .filter((item) => item.cadence !== 'Daily' && item.weekDay === getCurrentWeekDay())
    .sort((a, b) => {
      if (a.cadence !== b.cadence) return CHORE_CADENCES.indexOf(a.cadence) - CHORE_CADENCES.indexOf(b.cadence);
      return `${a.room} ${a.title}`.localeCompare(`${b.room} ${b.title}`);
    });

  const scheduledByRoom = scheduledChores.reduce((groups, item) => {
    const room = item.room || 'Unassigned room';
    groups[room] = [...(groups[room] || []), item];
    return groups;
  }, {});
  const rooms = Object.keys(scheduledByRoom).sort((a, b) => a.localeCompare(b));

  const budgetCategoryRows = data.budgetCategories
    .map((item) => ({ id: item.id, category: item.category, subcategory: item.subcategory, budgeted: Number(item.budgeted), actual: Number(item.actual) }))
    .sort((a, b) => b.actual - a.actual);

  const toggleRoomTasks = (room) => {
    const roomTasks = scheduledByRoom[room];
    const shouldComplete = roomTasks.some((item) => !item.doneToday);
    updateData((current) => ({
      ...current,
      chores: current.chores.map((item) => (
        roomTasks.some((rt) => rt.id === item.id)
          ? {
              ...item,
              doneToday: shouldComplete,
              lastCompletedDate: shouldComplete ? today : null,
              streak: Math.max(0, item.streak + (shouldComplete && !item.doneToday ? 1 : !shouldComplete && item.doneToday ? -1 : 0))
            }
          : item
      ))
    }));
  };

  const toggleDailyTasks = () => {
    const shouldComplete = dailyChores.some((item) => !item.doneToday);
    updateData((current) => ({
      ...current,
      chores: current.chores.map((item) => (
        dailyChores.some((dc) => dc.id === item.id)
          ? {
              ...item,
              doneToday: shouldComplete,
              lastCompletedDate: shouldComplete ? today : null,
              streak: Math.max(0, item.streak + (shouldComplete && !item.doneToday ? 1 : !shouldComplete && item.doneToday ? -1 : 0))
            }
          : item
      ))
    }));
  };

  return (
    <section className="view-grid">
      <section className="dashboard-top-grid wide">
        <div className="dashboard-left-stack">
          <div className="summary-grid dashboard-summary-grid">
            <Metric icon={Check} label="Today complete" value={`${stats.completed}/${stats.dueTodayCount}`} />
            <Metric icon={Banknote} label="Monthly spent" value={`$${stats.expenses.toFixed(0)}`} />
            <Metric icon={PackageCheck} label="Low inventory" value={stats.lowStock} />
            <Metric icon={Award} label="Best streak" value={`${stats.topStreak} days`} />
          </div>
          <section className="panel dashboard-budget-panel">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Budget pulse</p>
                <h3>This month</h3>
              </div>
            </div>
            <Progress label="Monthly budget" value={stats.expenses} max={data.monthlyBudget} />
            <Progress label="Weekly budget" value={stats.expenses / 4} max={data.weeklyBudget} />
          </section>
          <section className="panel dashboard-next-shop-panel">
            <div className="section-heading">
              <div>
                <p className="eyebrow">Next shop</p>
                <h3>Grouped list</h3>
              </div>
              <button className="text-button" onClick={() => setActiveView('shopping')} type="button">
                Open <ChevronRight size={15} />
              </button>
            </div>
            <StorePreview groceries={data.groceries} updateData={updateData} />
          </section>
        </div>
        <section className="panel dashboard-budget-tracker-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Budget categories</p>
              <h3>Category tracker</h3>
            </div>
            <button className="text-button" onClick={() => setActiveView('budget')} type="button">
              Budget <ChevronRight size={15} />
            </button>
          </div>
          <div className="budget-tracker-list">
            {budgetCategoryRows.map((item) => <BudgetTrackerRow key={item.id} item={item} />)}
          </div>
        </section>
        <section className="dashboard-reflection-stack">
          <ReadOnlyReflectionPanel icon={Sunrise} title="Morning" eyebrow="Identity entering the day" prompts={MORNING_REFLECTIONS} values={reflection.morning} />
          <ReadOnlyReflectionPanel icon={Moon} title="Evening" eyebrow="Gain and recalibration" prompts={EVENING_REFLECTIONS} values={reflection.evening} />
        </section>
      </section>

      <section className="panel wide dashboard-maintenance-panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Focus list</p>
            <h3>Today's maintenance</h3>
          </div>
          <button className="text-button" onClick={() => setActiveView('chores')} type="button">
            Chores <ArrowRight size={15} />
          </button>
        </div>
        <div className="dashboard-focus-grid">
          <div>
            <h4>Recurring</h4>
            <div className="task-stack dashboard-focus-list">
              <section className="room-task-group recurring-task-group">
                <div className="room-heading">
                  <button
                    className={dailyChores.length > 0 && dailyChores.every((item) => item.doneToday) ? 'check-circle checked' : 'check-circle'}
                    onClick={toggleDailyTasks}
                    type="button"
                    aria-label="Toggle daily tasks"
                  >
                    <Check size={14} />
                  </button>
                  <button className="room-toggle" onClick={() => setDailyOpen((v) => !v)} type="button" aria-expanded={dailyOpen}>
                    <span>Daily</span>
                    {dailyOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                </div>
                {dailyOpen && (
                  <div className="task-stack">
                    {dailyChores.map((item) => (
                      <TaskRow key={item.id} item={item} onToggle={() => toggleChore(item.id)} />
                    ))}
                  </div>
                )}
              </section>
            </div>
          </div>
          <div>
            <h4>Scheduled today</h4>
            <div className="task-stack dashboard-focus-list">
              {rooms.map((room) => (
                <section className="room-task-group" key={room}>
                  <div className="room-heading">
                    <button
                      className={scheduledByRoom[room].every((item) => item.doneToday) ? 'check-circle checked' : 'check-circle'}
                      onClick={() => toggleRoomTasks(room)}
                      type="button"
                      aria-label={`Toggle ${room} tasks`}
                    >
                      <Check size={14} />
                    </button>
                    <button className="room-toggle" onClick={() => setOpenRoom((r) => r === room ? '' : room)} type="button" aria-expanded={openRoom === room}>
                      <span>{room}</span>
                      {openRoom === room ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                  </div>
                  {openRoom === room && (
                    <div className="task-stack">
                      {scheduledByRoom[room].map((item) => (
                        <TaskRow key={item.id} item={item} onToggle={() => toggleChore(item.id)} />
                      ))}
                    </div>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}
